import * as amplitude from '@amplitude/analytics-browser';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import {Provider} from 'react-redux';
import {BrowserRouter as Router} from 'react-router-dom';
import './App.css';
import AppRoutes from './AppRoutes';
import BaseElem from './BaseElem';
import './Common.css';
import {Snackbar} from './Components/CommonComponents';
import {
  MSG_ON_PAGE_FOCUS,
  MSG_SET_LOADING_CONT_STYLE,
  MSG_SET_LOADING_STYLE,
  MSG_SHOW_SNACKBAR,
  SHOW_LOADING,
} from './Constants';
import configureStore from './store';
import {DesignSystemProvider} from './design-system/theme';

const store = configureStore();
const ONLINE = 'online';
const OFFLINE = 'offline';

type AppState = {
  isDisconnected: boolean;
  loading: number;
  loadingStyle?: any;
  loadingContStyle?: any;
  snackbars: any[];
};

class App extends BaseElem<any, AppState> {
  _bindConChange?: any;
  _bindOnFocus?: any;
  _bindOnBlur?: any;
  _bindVisibilityChange?: any;
  _isVisible = true;
  _hasInitializedAmplitude = false;

  constructor(props: any) {
    super(props);
    this.state = {
      isDisconnected: false,
      loading: 0,
      snackbars: [],
    };
    this._enableListeners(true);
    document.title = 'Smiley 2.0';
  }

  _enableListeners(isEnabled: boolean) {
    const self = this,
      win = window;
    const listener = isEnabled ? 'addEventListener' : 'removeEventListener';

    if (isEnabled && !self._bindConChange) {
      win._app = self;
      self._bindConChange = self._onConnectionChange.bind(self);
      self._bindOnFocus = self._onFocus.bind(self);
      self._bindOnBlur = self._onBlur.bind(self);
      self._bindVisibilityChange = self._onVisibilityChange.bind(self);
    }

    win[listener]('focus', self._bindOnFocus);
    win[listener]('blur', self._bindOnBlur);
    document[listener]('visibilitychange', self._bindVisibilityChange);

    win[listener](ONLINE, self._bindConChange);
    win[listener](OFFLINE, self._bindConChange);
    self.setListeners(isEnabled, SHOW_LOADING, MSG_SHOW_SNACKBAR, MSG_SET_LOADING_STYLE, MSG_SET_LOADING_CONT_STYLE);
    this._initializeAmplitude();
  }

  _initializeAmplitude() {
    if (!this._hasInitializedAmplitude && process.env.REACT_APP_AMPLITUDE) {
      amplitude.init(process.env.REACT_APP_AMPLITUDE);
      this._hasInitializedAmplitude = true;
    }
  }

  componentDidMount() {
    const self = this;
    self._onConnectionChange();
    self._enableListeners(true);
  }

  componentWillUnmount() {
    this._enableListeners(false);
  }

  _onFocus() {
    const self = this;
    self._isVisible = true;
    self.post(MSG_ON_PAGE_FOCUS);
  }

  _onBlur() {
    this.async(() => (this._isVisible = !document.hidden), 100);
  }

  _onVisibilityChange() {
    this[document.hidden ? '_onBlur' : '_onFocus']();
  }

  onMessage(messageName: string, payload?: any) {
    const self = this,
      state = self.state;
    if (messageName === SHOW_LOADING) {
      self.setState(previousState => ({loading: previousState.loading + (payload || 0)}));
    } else if (messageName === MSG_SET_LOADING_STYLE) {
      self.setState({loadingStyle: payload});
    } else if (messageName === MSG_SET_LOADING_CONT_STYLE) {
      self.setState({loadingContStyle: payload});
    } else if (payload && messageName === MSG_SHOW_SNACKBAR) {
      const snackbars = state.snackbars;
      if (snackbars.find(sb => !sb.actionTitle && sb.message === payload.message)) return;
      payload.open = true;
      snackbars.push(payload);
      self.setState({snackbars: snackbars});

      const duration = payload.duration;
      duration && self.async(() => self._removeSnackbar(payload), duration);
    }
  }

  _removeSnackbar(snackbar: any) {
    const self = this,
      snackbars = self.state.snackbars;
    snackbar.open = false;
    self.setState({snackbars: snackbars});
    self.async(() => self.setState({snackbars: snackbars.filter(s => s !== snackbar)}), 500);
    return true;
  }

  _onConnectionChange() {
    this.setState({isDisconnected: !navigator.onLine});
  }

  render() {
    const self = this,
      state = self.state;
    return (
      <DesignSystemProvider>
          <Provider store={store}>
            <div className="app vertical">
            <Router>
              <AppRoutes />
            </Router>

            <LoadingSpinner loading={state.loading} style={state.loadingStyle} />

            <SnackbarContainer
              isDisconnected={state.isDisconnected}
              snackbars={state.snackbars}
              onRemove={self._removeSnackbar.bind(self)}
            />
            </div>
          </Provider>
        </DesignSystemProvider>
    );
  }
}

const LoadingSpinner: React.FC<{loading: number; style?: any}> = ({loading, style}) => (
  <div className={'loading-spinner' + (loading <= 0 ? ' gone' : '')} style={style}>
    <div className="app-spinner-cont">
      <CircularProgress color="primary" />
    </div>
  </div>
);

const SnackbarContainer: React.FC<{
  isDisconnected: boolean;
  snackbars: any[];
  onRemove: (snackbar: any) => void;
}> = ({isDisconnected, snackbars, onRemove}) => (
  <div className="app-snackbars">
    <Snackbar noClose variant="info" isOpen={isDisconnected} message="No Internet Connection" />
    {snackbars.map(sb => (
      <Snackbar key={sb.key} {...sb} noClose={!!sb.duration} onClose={() => onRemove(sb)} />
    ))}
  </div>
);

export default App;
